import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import NumericInput from 'react-numeric-input';
import { toast } from 'react-toastify';

import InputWrapper from '../common/InputWrapper';
import Button from '../common/Button';
import Checkbox from '../common/Checkbox';
import Select from '../common/Select';
import Radios from '../common/Radios';

import { fetchFindCity } from '../../actions/delivery';
import { showModal } from '../../actions/modal';

import { preventSubmittingByEnter, getCurrencySymbol } from '../../helpers';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const CheckoutForm = styled('form')`
  .react-numeric-input {
    flex: 1 1;
    input {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`;

export const freeDeliveryRegionData = [
  {
    label: 'Россия',
    value: 'RU',
  },
  {
    label: 'Россия и СНГ',
    value: 'RU_SNG',
  },
  {
    label: 'Все страны',
    value: 'ALL',
  },
];

const CheckoutBlock = (props) => {
  const { values, handleChangeInput, handleSubmit } = props;

  const dispatch = useDispatch();
  const [cacheCityOptions, setCacheCityOptions] = useState([]);
  const { checkoutSettings } = useSelector((state) => state.settings);

  const { t } = useAutoTranslation();

  useEffect(() => {
    loadCityOptions(values.defaultCity);
  }, []);

  const loadCityOptions = (value) =>
    new Promise(async (resolve) => {
      if (value && value.length > 2) {
        const result = await dispatch(fetchFindCity(value));
        const newCityOptions = result.map((c) => ({
          label: t(c.value),
          value: c.value,
          data: c,
        }));
        setCacheCityOptions(newCityOptions);
        resolve(newCityOptions);
      } else {
        resolve([]);
      }
    });

  const cityValue = {
    label: values.defaultCity,
    value: values.defaultCity,
  };

  const handleOpenPolicityLinks = () => {
    dispatch(
      showModal('PolicityLinks', {
        settingsState: { ...values },
        submitCallback: ({ personalInfoPolicyLink, confidentialPolicyLink }) => {
          handleChangeInput('personalInfoPolicyLink', personalInfoPolicyLink);
          handleChangeInput('confidentialPolicyLink', confidentialPolicyLink);
          handleSubmit();
        },
      }),
    );
  };

  const handleUpdateUnlockOrderDuringOnlinePayment = (event) => {
    if (event.target.checked)
      toast.warn(`Настройка снимает блокировку на изменение заказа до выгрузки на склад`);
    handleUpdateCheckbox(event);
  };

  const handleUpdateCheckbox = (e) => handleChangeInput(e.target.id, e.target.checked);
  const currencySymbol = getCurrencySymbol(checkoutSettings.currency);

  return (
    <CheckoutForm onKeyDown={preventSubmittingByEnter} onSubmit={handleSubmit}>
      <div className="border-bottom">
        <h4 className="header-title">{t('Оформление заказа')}</h4>
      </div>

      <div className="row" style={{ marginTop: '16px' }}>
        <div className="col-6">
          <div className="form-group">
            <h5>{t('Обязательные к заполнению поля формы заказа')}</h5>
            <Checkbox
              label={t('Телефон')}
              id="uiRequiredPhone"
              checked={values.uiRequiredPhone}
              onChange={handleUpdateCheckbox}
              inlineLayout
            />
            <Checkbox
              label={t('Email')}
              id="uiRequiredEmail"
              checked={values.uiRequiredEmail}
              onChange={handleUpdateCheckbox}
              inlineLayout
            />
          </div>

          <div className="form-group">
            <Select
              id="defaultCity"
              label={t('Город доставки по умолчанию')}
              isAsync
              isClearable
              defaultOptions={cacheCityOptions}
              loadOptions={loadCityOptions}
              value={cityValue}
              onChange={(value) => handleChangeInput('defaultCity', value?.value ?? '')}
              noOptionsMessage={t('Введите название населенного пункта')}
            />
          </div>

          <div className="form-group">
            <InputWrapper
              type="text"
              label={t('Сумма заказа для бесплатной доставки')}
              id="freeDeliveryAmount"
              value={values.freeDeliveryAmount ? values.freeDeliveryAmount.toFixed(2) : '0'}
              onChange={(valueAsNumber) => handleChangeInput('freeDeliveryAmount', valueAsNumber)}
              textAppend={currencySymbol}
              component={NumericInput}
              componentProps={{ precision: 2, style: false }}
            />
          </div>

          <div className="form-group">
            <Radios
              inlineLayout
              data={freeDeliveryRegionData}
              id="freeDeliveryRegion"
              name="freeDeliveryRegion"
              currentValue={t(values.freeDeliveryRegion)}
              onChange={(value) => handleChangeInput('freeDeliveryRegion', value)}
            />
          </div>

          <div className="form-group">
            <InputWrapper
              type="text"
              label={t('После оформления заказа переводить на страницу')}
              labelQuestion={t(
                'Настройка работает только с "Умной корзиной". Укажите адрес страницы на которую необходимо перевести покупателя после оформления заказа.',
              )}
              id="successRedirectUrl"
              value={values.successRedirectUrl}
              onChange={(e) => handleChangeInput('successRedirectUrl', e.target.value)}
            />
          </div>

          <div className="form-group">
            <InputWrapper
              type="text"
              label={t('Адрес ЭДО')}
              id="ediAddress"
              value={values.ediAddress}
              onChange={(e) => handleChangeInput('ediAddress', e.target.value)}
            />
          </div>
        </div>

        <div className="col-6">
          <div className="form-group">
            <h5>{t('Дополнительные настройки')}</h5>
            <Checkbox
              label={t('Запрет покупки товара с нулевыми остатками')}
              id="onlySufficientOffers"
              checked={values.onlySufficientOffers}
              onChange={handleUpdateCheckbox}
            />
          </div>

          <div className="form-group">
            <Checkbox
              label={t('Оформлять заказ подтвержденным')}
              id="importOrderConfirmed"
              checked={values.importOrderConfirmed}
              onChange={handleUpdateCheckbox}
            />
          </div>

          <div className="form-group">
            <Checkbox
              label={t('Не блокировать изменения в заказе при оплате онлайн')}
              id="unlockOrderDuringOnlinePayment"
              checked={values.unlockOrderDuringOnlinePayment}
              onChange={handleUpdateUnlockOrderDuringOnlinePayment}
            />
          </div>

          <div className="form-group">
            <Checkbox
              label={t('Не включать процент за прием наложенного платежа в стоимость доставки')}
              id="disableCodSumInTariff"
              checked={values.disableCodSumInTariff}
              onChange={handleUpdateCheckbox}
            />
          </div>

          <div className="form-group">
            <Checkbox
              label={t('Не включать процент за страховку в стоимость доставки')}
              id="disableInsuranceSumInTariff"
              checked={values.disableInsuranceSumInTariff}
              onChange={handleUpdateCheckbox}
            />
          </div>

          <div className="form-group">
            <Checkbox
              label={t('Запрашивать у покупателя согласие на информационную рассылку')}
              id="showConsentToNewsletter"
              checked={values.showConsentToNewsletter}
              onChange={handleUpdateCheckbox}
            />
          </div>

          <div className="form-group">
            <Checkbox
              label={t(
                'Использовать ссылки на политики конфиденциальности и обработки персональных данных',
              )}
              id="policyCheckEnabled"
              checked={values.policyCheckEnabled}
              onChange={handleUpdateCheckbox}
            />
          </div>
          <div className="form-group">
            <Checkbox
              label={t('Резервировать в случае онлайн-оплаты - после поступления оплаты')}
              labelQuestion={t(
                'В случае включения опции существует риск оплаты последних остатков товара несколькими покупателями',
              )}
              id="reserveAfterPayment"
              checked={values.reserveAfterPayment}
              onChange={handleUpdateCheckbox}
            />
          </div>
          <div className="form-group">
            <Checkbox
              label={t('Выводить в корзину опции "Примерка", "Частичный выкуп"')}
              id="showDeliveryOptions"
              checked={values.showDeliveryOptions}
              onChange={handleUpdateCheckbox}
            />
          </div>
          {/* <div className="form-group">
            <Checkbox
              label="Резервировать заказы по умолчанию"
              id="defaultReserveOrders"
              checked={values.defaultReserveOrders}
              onChange={handleUpdateCheckbox}
            />
          </div> */}
          <div className="form-group">
            <Checkbox
              label={t('Не включать услугу ПРР в стоимость доставки')}
              id="disablePRRInTariff"
              checked={values.disablePRRInTariff}
              onChange={handleUpdateCheckbox}
            />
          </div>
          {values.policyCheckEnabled && (
            <Button
              classType="btn-light"
              text={t('Добавить ссылки')}
              onClick={handleOpenPolicityLinks}
            />
          )}
        </div>
      </div>
    </CheckoutForm>
  );
};

export default CheckoutBlock;
